/**
 * Specs tabs — tab switch + live variant option values (by name / opt 1–3 + optional split) + info modal
 */
(function () {
  function norm(str) {
    return String(str || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function findOptionIndex(optionNames, matchName) {
    const target = norm(matchName);
    if (!target) return -1;
    let idx = optionNames.findIndex((n) => norm(n) === target);
    if (idx >= 0) return idx;
    idx = optionNames.findIndex((n) => {
      const v = norm(n);
      return v.includes(target) || target.includes(v);
    });
    return idx;
  }

  function readSelectedFromDom(root) {
    const productForm = root.closest('product-info') || document;
    const picks = {};
    productForm.querySelectorAll('[data-selected-swatch-value]').forEach((el) => {
      const name = el.getAttribute('data-selected-swatch-value');
      if (!name) return;
      picks[name] = (el.textContent || '').trim();
    });
    return picks;
  }

  function getVariantOptions(root) {
    let optionNames = [];
    try {
      optionNames = JSON.parse(root.getAttribute('data-option-names') || '[]');
    } catch (e) {
      optionNames = [];
    }

    const picks = readSelectedFromDom(root);
    const variantEl = (root.closest('product-info') || document).querySelector(
      'variant-selects [data-selected-variant]'
    );
    let variantOptions = null;
    if (variantEl) {
      try {
        const variant = JSON.parse(variantEl.textContent);
        if (variant && Array.isArray(variant.options)) variantOptions = variant.options;
      } catch (e) {
        /* ignore */
      }
    }

    const getAt = (idx) => {
      if (idx < 0) return '';
      if (variantOptions && variantOptions[idx] != null) return String(variantOptions[idx]).trim();
      const name = optionNames[idx];
      return picks[name] || '';
    };

    return { optionNames, getAt };
  }

  function applySplit(raw, split, divider) {
    const value = String(raw || '').trim();
    if (!value) return '';
    const mode = split || 'none';
    if (mode === 'none') return value;
    const sep = divider && String(divider).length ? String(divider) : '|';
    const parts = value.split(sep).map((p) => p.trim());
    if (mode === 'left') return parts[0] || '';
    if (mode === 'right') return parts[1] || '';
    return value;
  }

  function resolveLiveValue(el, ctx) {
    const source = el.getAttribute('data-specs-source') || 'auto';
    const match = el.getAttribute('data-specs-match') || '';
    const split = el.getAttribute('data-specs-split') || 'none';
    const divider = el.getAttribute('data-specs-divider') || '|';
    const fallback = (el.getAttribute('data-specs-fallback') || '').trim();

    let raw = '';
    if (source === 'opt1') raw = ctx.getAt(0);
    else if (source === 'opt2') raw = ctx.getAt(1);
    else if (source === 'opt3') raw = ctx.getAt(2);
    else if (source === 'by_name') {
      raw = ctx.getAt(findOptionIndex(ctx.optionNames, match));
    }

    const live = applySplit(raw, split, divider);
    return (live || fallback).trim();
  }

  function applyVariantValues(root) {
    if (!root || root._specsApplying) return;
    root._specsApplying = true;
    try {
      const ctx = getVariantOptions(root);
      root.querySelectorAll('[data-specs-live]').forEach((el) => {
        const next = resolveLiveValue(el, ctx);
        if (el.textContent !== next) el.textContent = next;
        const row = el.closest('[data-specs-row]');
        if (row) {
          if (next) row.removeAttribute('hidden');
          else row.setAttribute('hidden', '');
        }
      });
    } finally {
      root._specsApplying = false;
    }
  }

  function scheduleApply(root) {
    if (!root) return;
    if (root._specsTimer) window.clearTimeout(root._specsTimer);
    root._specsTimer = window.setTimeout(() => {
      root._specsTimer = null;
      applyVariantValues(root);
    }, 50);
  }

  class ProductSpecsTabs extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this._activeTipBtn = null;
      this._modal = this.querySelector('[data-specs-modal]');
      const inEditor = Boolean(window.Shopify?.designMode);

      if (this._modal && this._modal.parentElement !== document.body) {
        document.body.appendChild(this._modal);
      }

      if (this._modal) {
        this._onModalClick = (e) => {
          if (e.target.closest('[data-specs-modal-close]')) {
            e.preventDefault();
            this.closeTipModal();
            return;
          }
          if (!e.target.closest('[data-specs-modal-dialog]')) {
            e.preventDefault();
            this.closeTipModal();
          }
        };
        this._modal.addEventListener('click', this._onModalClick);
      }

      this.addEventListener('click', (e) => {
        const tipBtn = e.target.closest('[data-specs-tip]');
        if (tipBtn && this.contains(tipBtn)) {
          e.preventDefault();
          e.stopPropagation();
          this.openTipModal(tipBtn);
          return;
        }

        const btn = e.target.closest('[data-specs-tab]');
        if (!btn || !this.contains(btn)) return;
        const tabId = btn.getAttribute('data-specs-tab');
        this.querySelectorAll('[data-specs-tab]').forEach((b) => {
          const on = b === btn;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        this.querySelectorAll('[data-specs-panel]').forEach((panel) => {
          const on = panel.getAttribute('data-specs-panel') === tabId;
          if (on) panel.removeAttribute('hidden');
          else panel.setAttribute('hidden', '');
        });
      });

      this._onKeydown = (e) => {
        if (e.key === 'Escape') this.closeTipModal();
      };
      document.addEventListener('keydown', this._onKeydown);

      this._onVariant = () => scheduleApply(this);
      document.addEventListener('variant:changed', this._onVariant);
      if (window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.variantChange) {
        this._unsub = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          this._onVariant
        );
      }

      // MutationObserver thrash Theme Editor on setting paints — skip in designMode
      if (!inEditor) {
        this._mo = new MutationObserver(() => scheduleApply(this));
        const info = this.closest('product-info') || document.body;
        info.querySelectorAll('[data-selected-swatch-value]').forEach((el) => {
          this._mo.observe(el, { childList: true, characterData: true, subtree: true });
        });
      }

      applyVariantValues(this);
    }

    openTipModal(tipBtn) {
      const modal = this._modal || this.querySelector('[data-specs-modal]');
      if (!modal) return;
      this._modal = modal;
      const titleEl = modal.querySelector('[data-specs-modal-title]');
      const bodyEl = modal.querySelector('[data-specs-modal-body]');
      const dialog = modal.querySelector('[data-specs-modal-dialog]');
      if (titleEl) titleEl.textContent = tipBtn.getAttribute('data-specs-tip-title') || '';
      if (bodyEl) bodyEl.textContent = tipBtn.getAttribute('data-specs-tip-body') || '';

      this.querySelectorAll('[data-specs-tip]').forEach((b) => {
        b.setAttribute('aria-expanded', b === tipBtn ? 'true' : 'false');
      });
      this._activeTipBtn = tipBtn;

      document.documentElement.classList.add('bb-specs-modal-open');
      modal.removeAttribute('hidden');
      requestAnimationFrame(() => {
        modal.classList.add('is-open');
        dialog?.focus?.({ preventScroll: true });
      });
    }

    closeTipModal() {
      const modal = this._modal || this.querySelector('[data-specs-modal]');
      if (!modal || modal.hasAttribute('hidden')) return;
      modal.classList.remove('is-open');
      this.querySelectorAll('[data-specs-tip]').forEach((b) => {
        b.setAttribute('aria-expanded', 'false');
      });
      document.documentElement.classList.remove('bb-specs-modal-open');
      const prev = this._activeTipBtn;
      this._activeTipBtn = null;
      window.setTimeout(() => {
        modal.setAttribute('hidden', '');
        prev?.focus?.({ preventScroll: true });
      }, 180);
    }

    disconnectedCallback() {
      document.removeEventListener('variant:changed', this._onVariant);
      document.removeEventListener('keydown', this._onKeydown);
      document.documentElement.classList.remove('bb-specs-modal-open');
      if (this._modal && this._onModalClick) {
        this._modal.removeEventListener('click', this._onModalClick);
      }
      if (this._unsub) this._unsub();
      if (this._mo) this._mo.disconnect();
      if (this._specsTimer) window.clearTimeout(this._specsTimer);
    }
  }

  if (!customElements.get('product-specs-tabs')) {
    customElements.define('product-specs-tabs', ProductSpecsTabs);
  }
})();
